/**
 * Formulation 会话加载逻辑
 */
import type { Ref } from 'vue';
import type { Router } from 'vue-router';
import type { AgentMessageResponse } from '@/types/formulation/formulation';
import type { MaterialFormulationApi } from '@/domains/material/apis/formulation-api';
import { STREAMING_MESSAGE_ID, TASK_STATUS, isTaskInProgress, isTaskJustStarted } from './types';

interface PollMessagesDeps {
  sessionIdParam: string;
  sessionId: Ref<string>;
  isLoadingConversation: Ref<boolean>;
  pipelineRunning: Ref<boolean>;
  taskStatus: Ref<string>;
  conversationData: Ref<AgentMessageResponse[] | undefined>;
  materialFormulationApiService: MaterialFormulationApi;
  tryAutoOpenLastToolResult: (list: AgentMessageResponse[] | undefined) => void;
}

interface MessagePoller {
  start: (targetSessionId: string) => void;
  stop: () => void;
}

/**
 * 创建消息轮询器，支持显式停止
 */
function createMessagePoller(deps: Omit<PollMessagesDeps, 'sessionIdParam'>): MessagePoller {
  const {
    sessionId,
    isLoadingConversation,
    pipelineRunning,
    taskStatus,
    conversationData,
    materialFormulationApiService,
    tryAutoOpenLastToolResult,
  } = deps;

  const pollIntervalMs = 2000;
  const maxPollAttempts = 300;

  let pollTimerId: ReturnType<typeof setTimeout> | null = null;
  let isPollingActive = false;
  let currentTargetSessionId = '';
  let attempts = 0;

  const poll = async () => {
    // 轮询已停止或 session 已切换，退出
    if (!isPollingActive || sessionId.value !== currentTargetSessionId) {
      return;
    }
    // 如果流式连接正在进行，跳过本次轮询
    if (pipelineRunning.value) {
      pollTimerId = setTimeout(poll, pollIntervalMs);
      return;
    }
    if (attempts >= maxPollAttempts) {
      return;
    }
    attempts += 1;

    try {
      const nextTask = await materialFormulationApiService.getUserTask(currentTargetSessionId);
      if (sessionId.value !== currentTargetSessionId) return;

      taskStatus.value = nextTask.status ?? '';

      if (nextTask.status !== TASK_STATUS.RUNNING) {
        pipelineRunning.value = false;
        try {
          const finalList =
            await materialFormulationApiService.listAgentMessages(currentTargetSessionId);
          if (sessionId.value === currentTargetSessionId && finalList.length > 0) {
            conversationData.value = finalList;
            tryAutoOpenLastToolResult(finalList);
          }
          isLoadingConversation.value = false;
        } catch {
          // ignore
        }
        return;
      }
    } catch {
      // ignore
    }

    if (isPollingActive) {
      pollTimerId = setTimeout(poll, pollIntervalMs);
    }
  };

  const stop = () => {
    isPollingActive = false;
    if (pollTimerId) {
      clearTimeout(pollTimerId);
      pollTimerId = null;
    }
  };

  const start = (targetSessionId: string) => {
    // 停止之前的轮询
    stop();
    currentTargetSessionId = targetSessionId;
    isPollingActive = true;
    attempts = 0;
    pollTimerId = setTimeout(poll, pollIntervalMs);
  };

  return { start, stop };
}

/** getConversation 函数所需的依赖 */
export interface ConversationLoaderDeps {
  /** 会话 ID */
  sessionId: Ref<string>;
  contextId: Ref<number>;
  /** 刚创建的 session ID，用于避免重复拉取 */
  justCreatedSessionId: Ref<string>;
  /** 是否正在加载会话 */
  isLoadingConversation: Ref<boolean>;
  /** pipeline 是否正在运行 */
  pipelineRunning: Ref<boolean>;
  /** 实时任务状态 */
  taskStatus: Ref<string>;
  /** 会话数据 */
  conversationData: Ref<AgentMessageResponse[] | undefined>;
  /** 任务标题（共享给 Header 使用） */
  userTaskTitle: Ref<string>;
  /** API 服务 */
  materialFormulationApiService: MaterialFormulationApi;
  /** 流式会话函数 */
  streamConversation: (
    sessionIdValue: string,
    contextIdValue: Ref<number> | undefined,
    content: string,
    options?: { skipAddUserMessage?: boolean },
  ) => Promise<void>;
  /** 自动打开最后一个工具结果 */
  tryAutoOpenLastToolResult: (list: AgentMessageResponse[] | undefined) => void;
  /** 路由实例 */
  router: Router;
}

/**
 * 创建 getConversation 函数
 */
export function createGetConversation(deps: ConversationLoaderDeps) {
  const {
    sessionId,
    contextId,
    justCreatedSessionId,
    isLoadingConversation,
    pipelineRunning,
    taskStatus,
    conversationData,
    userTaskTitle,
    materialFormulationApiService,
    streamConversation,
    tryAutoOpenLastToolResult,
    router,
  } = deps;

  // 创建消息轮询器（单例，切换 session 时会自动停止旧的轮询）
  const messagePoller = createMessagePoller({
    sessionId,
    isLoadingConversation,
    pipelineRunning,
    taskStatus,
    conversationData,
    materialFormulationApiService,
    tryAutoOpenLastToolResult,
  });

  /**
   * 获取会话数据
   * - 如果任务正在运行，则连接流式接口并轮询消息
   * - 如果任务已完成，则直接获取消息列表
   */
  const getConversation = async (sessionIdParam: string) => {
    if (justCreatedSessionId.value === sessionIdParam) {
      justCreatedSessionId.value = '';
    }

    // 切换 session 时停止旧的轮询
    messagePoller.stop();

    isLoadingConversation.value = true;
    sessionId.value = sessionIdParam;

    try {
      const userTask = await materialFormulationApiService.getUserTask(sessionIdParam);
      taskStatus.value = userTask.status ?? '';
      userTaskTitle.value = userTask.title ?? '';

      if (isTaskInProgress(userTask.status)) {
        // 任务进行中：使用流式连接，不启动轮询（避免两者同时更新导致闪烁）
        pipelineRunning.value = true;

        if (isTaskJustStarted(userTask.status)) {
          conversationData.value = [];
          streamConversation(sessionIdParam, contextId, userTask.title).catch((err) => {
            console.error('[Formulation] Stream error in getConversation:', err);
            pipelineRunning.value = false;
          });
        } else {
          conversationData.value = [];
          streamConversation(sessionIdParam, undefined, '', { skipAddUserMessage: true }).catch(
            (err) => {
              console.error('[Formulation] Stream error in getConversation:', err);
              pipelineRunning.value = false;
            },
          );
        }
        // 流式接口：getUserTask 返回后即设置为 false
        isLoadingConversation.value = false;
      } else {
        // 任务已完成：立即获取历史消息
        pipelineRunning.value = false;
        try {
          const messageList = await materialFormulationApiService.listAgentMessages(sessionIdParam);
          if (sessionId.value === sessionIdParam && messageList.length > 0) {
            conversationData.value = messageList;
            tryAutoOpenLastToolResult(messageList);
          }
        } catch {
          // ignore
        }
        isLoadingConversation.value = false;
      }
    } catch (error) {
      console.error('[Formulation] Failed to get conversation:', error);
      isLoadingConversation.value = false;
      router.push({ name: 'Home' });
    }
  };

  return getConversation;
}
